import {
  DM_FUEL_REGEN,
  DM_RELOAD_MS,
  DM_SHELLS,
  DM_START_GRACE_MS,
  DM_TIME_LIMIT_MS,
  DM_WIND_PERIOD_MS,
  FALL_DURATION,
  FUEL,
  MAX_ANGLE,
  MAX_HP,
  MIN_ANGLE,
  MAX_WIND,
  TANK_COLORS,
  WALK_SPEED,
  WATER_Y,
  WORLD_W,
} from './constants';
import { createBlaster, settleTanks, settleY, simulateShot, tankCenter, walk } from './physics';
import { hashInts, mulberry32 } from './rng';
import { buildTerrain, generateTerrain, type Terrain, type TerrainStyle } from './terrain';
import type { DeathCause, GameState, Tank, TurnEvents, TurnInput } from './types';
import { ammoOf, dmAmmo, GUIDED_MAX_RUNS, GUIDED_MAX_STEPS, KAMIKAZE_CODES, KAMIKAZE_MAX_RUNS, KAMIKAZE_MAX_STEPS, BOMBER_MAX_RUNS, BOMBER_MAX_STEPS, initialAmmo, MINE_TRIGGER, totalAmmo, WEAPONS } from './weapons';
import type { BotLevel, TankLook } from './appearance';
import { WORLDS } from './worlds';

export function rollWind(seed: number, turn: number, style: TerrainStyle = 'hills'): number {
  const r = mulberry32(hashInts(seed, turn, 0x77))();
  // Měsíc je bez větru, na Marsu jsou silné prachové poryvy
  return Math.round((r * 2 - 1) * MAX_WIND * WORLDS[style].wind) || 0;
}

export function createGameState(
  seed: number,
  style: TerrainStyle,
  players: { id: string; name: string; look?: TankLook | null; bot?: BotLevel | null }[],
  /** Deathmatch v reálném čase: serverový čas startu. */
  deathmatch?: { now: number },
): GameState {
  const terrain = generateTerrain(seed, style);
  const rng = mulberry32(hashInts(seed, 0x51a7));
  // Náhodné rozmístění hráčů do slotů
  const order = players.slice();
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const slot = WORLD_W / order.length;
  const tanks: Tank[] = order.map((p, i) => {
    let x = Math.floor(slot * (i + 0.5) + (rng() - 0.5) * slot * 0.4);
    x = Math.max(30, Math.min(WORLD_W - 30, x));
    return {
      id: p.id,
      name: p.name,
      color: p.look?.color ?? TANK_COLORS[players.indexOf(p) % TANK_COLORS.length],
      ...(p.look ? { look: p.look } : {}),
      ...(p.bot ? { bot: p.bot } : {}),
      x,
      y: Math.min(terrain.surfaceY(x), WATER_Y - 20),
      hp: MAX_HP,
      alive: true,
      angle: x < WORLD_W / 2 ? 60 : 120,
      power: 60,
      ammo: initialAmmo(),
    };
  });
  // Kdo začíná, je taky náhoda
  const current = Math.floor(rng() * tanks.length);
  const state: GameState = { seed, style, ops: [], tanks, current, turn: 1, wind: rollWind(seed, 1, style), status: 'playing', winner: null };
  if (deathmatch) {
    const { now } = deathmatch;
    state.dm = { startedAt: now, endsAt: now + DM_TIME_LIMIT_MS, scores: {} };
    for (const t of tanks) {
      t.ammo = dmAmmo(DM_SHELLS);
      t.fuel = FUEL;
      t.fuelAt = now;
      t.reloadAt = now + DM_START_GRACE_MS;
      state.dm.scores[t.id] = { kills: 0, damage: 0 };
    }
    state.wind = dmWind(state, now);
  }
  return state;
}

export function stateTerrain(state: GameState): Terrain {
  return buildTerrain(state.seed, state.style, state.ops);
}

export function currentTank(state: GameState): Tank | undefined {
  return state.status === 'playing' ? state.tanks[state.current] : undefined;
}

export class TurnError extends Error {}

function emptyEvents(kind: TurnEvents['kind'], playerId: string): TurnEvents {
  return { kind, playerId, projectiles: [], explosions: [], ops: [], splashes: [], teleports: [], damage: [], falls: [], deaths: [], duration: 0 };
}

export function validateInput(state: GameState, playerId: string, input: TurnInput): Tank {
  if (state.status !== 'playing') throw new TurnError('Hra už skončila.');
  if (state.dm) throw new TurnError('V deathmatchi se nehraje na tahy.');
  const tank = state.tanks[state.current];
  if (!tank || tank.id !== playerId) throw new TurnError('Nejsi na tahu.');
  checkInput(tank, input, FUEL);
  return tank;
}

function checkInput(tank: Tank, input: TurnInput, maxMove: number) {
  if (!input || typeof input !== 'object') throw new TurnError('Neplatný tah.');
  const { move, angle, power, weapon } = input;
  if (!Number.isInteger(move) || Math.abs(move) > maxMove) throw new TurnError(maxMove < FUEL ? 'Málo paliva.' : 'Neplatný pohyb.');
  if (typeof angle !== 'number' || !(angle >= MIN_ANGLE && angle <= MAX_ANGLE)) throw new TurnError('Neplatný úhel.');
  if (typeof power !== 'number' || !(power >= 0 && power <= 100)) throw new TurnError('Neplatná síla.');
  if (typeof weapon !== 'string' || !(weapon in WEAPONS)) throw new TurnError('Neznámá zbraň.');
  if (ammoOf(tank.ammo, weapon) === 0) throw new TurnError('Došla munice.');
  const steer = input.steer;
  if (steer !== undefined) {
    const kami = weapon === 'kamikaze';
    const bomber = weapon === 'bomber';
    if ((weapon !== 'guided' && !kami && !bomber) || !Array.isArray(steer) || steer.length > (kami ? KAMIKAZE_MAX_RUNS : bomber ? BOMBER_MAX_RUNS : GUIDED_MAX_RUNS)) throw new TurnError('Neplatné řízení střely.');
    let total = 0;
    for (const run of steer) {
      if (!Array.isArray(run) || run.length !== 2) throw new TurnError('Neplatné řízení střely.');
      const [n, d] = run;
      const okDir = kami ? Number.isInteger(d) && d >= 0 && d < KAMIKAZE_CODES : bomber ? d === 0 || d === 1 : d === -1 || d === 0 || d === 1;
      if (!Number.isInteger(n) || n <= 0 || !okDir) throw new TurnError('Neplatné řízení střely.');
      total += n;
    }
    if (total > (kami ? KAMIKAZE_MAX_STEPS : bomber ? BOMBER_MAX_STEPS : GUIDED_MAX_STEPS)) throw new TurnError('Neplatné řízení střely.');
  }
}

/**
 * Odehraje tah. `terrain` musí odpovídat `state.ops` a je upraven na místě;
 * vstupní `state` se nemění.
 */
export function resolveTurn(
  state: GameState,
  terrain: Terrain,
  playerId: string,
  input: TurnInput,
): { state: GameState; events: TurnEvents } {
  validateInput(state, playerId, input);
  const next: GameState = structuredClone(state);
  const tank = next.tanks[next.current];
  const ev = emptyEvents('shot', playerId);
  const cleanInput = sanitizeInput(input);
  ev.input = cleanInput;
  ev.duration = playAction(next, terrain, tank, cleanInput, ev, true);
  finishTurn(next);
  return { state: next, events: ev };
}

function sanitizeInput(input: TurnInput): TurnInput {
  return {
    move: input.move,
    angle: Math.round(input.angle * 10) / 10,
    power: Math.round(input.power * 10) / 10,
    weapon: input.weapon,
    ...((input.weapon === 'guided' || input.weapon === 'kamikaze' || input.weapon === 'bomber') && input.steer
      ? { steer: input.steer.map(([n, d]) => [n, d] as [number, number]) }
      : {}),
  };
}

/**
 * Jízda a (volitelně) výstřel jednoho tanku, včetně pádů a min. Mění `next`
 * i `terrain` na místě, opy přidá do `next.ops`. Vrací délku přehrávání (s).
 */
function playAction(next: GameState, terrain: Terrain, tank: Tank, cleanInput: TurnInput, ev: TurnEvents, shoot: boolean): number {
  tank.angle = cleanInput.angle;
  tank.power = cleanInput.power;

  const mines = (next.mines ??= []);
  let t = 0;
  if (cleanInput.move !== 0) {
    const w = walk(terrain, tank.x, tank.y, cleanInput.move < 0 ? -1 : 1, Math.abs(cleanInput.move));
    // Najede-li tank k aktivní mině, zastaví se na ní (vybuchne na začátku výstřelu)
    const armed = mines.filter((m) => m.armedTurn <= next.turn);
    if (armed.length) {
      for (let i = 0; i < w.path.length; i += 2) {
        const c = tankCenter({ x: w.path[i], y: w.path[i + 1] });
        if (armed.some((m) => Math.hypot(c.x - m.x, c.y - (m.y - 4)) < MINE_TRIGGER)) {
          w.path.length = i + 2;
          w.x = w.path[i];
          w.y = w.path[i + 1];
          w.drowned = false;
          break;
        }
      }
    }
    const duration = w.path.length / 2 / WALK_SPEED;
    ev.walk = { path: w.path, duration };
    tank.x = w.x;
    tank.y = w.y;
    t = duration;
    if (w.drowned) {
      tank.alive = false;
      ev.deaths.push({ t, id: tank.id, cause: 'water' });
    }
  }

  let end = t;
  if (tank.alive) {
    if (shoot) {
      t += 0.3; // krátká pauza před výstřelem
      const left = ammoOf(tank.ammo, cleanInput.weapon);
      if (left > 0) tank.ammo[cleanInput.weapon] = left - 1;
      end = simulateShot(terrain, next.tanks, tank, cleanInput, next.wind, t, ev, mines, next.turn);
    }
    end += 0.4;
    if (settleTanks(terrain, next.tanks, end, ev)) end += FALL_DURATION;
    // Tank spadl na minu → výbuch, případně další pády (max. 3 kola)
    const blast = createBlaster(terrain, next.tanks, ev, mines, next.turn);
    for (let round = 0; round < 3 && blast.checkMines(end); round++) {
      end = Math.max(end, blast.last) + 0.3;
      if (settleTanks(terrain, next.tanks, end, ev)) end += FALL_DURATION;
    }
    blast.settleMines(end);
  }
  for (const { op } of ev.ops) next.ops.push(op);
  return end + 0.6;
}

// ---------- Deathmatch v reálném čase ----------

/** Palivo tanku v čase `now` (dobíjí se až do FUEL). */
export function dmFuel(tank: Tank, now: number): number {
  const base = tank.fuel ?? FUEL;
  const since = Math.max(0, now - (tank.fuelAt ?? now));
  return Math.min(FUEL, Math.floor(base + (since / 1000) * DM_FUEL_REGEN));
}

function dmWind(state: GameState, now: number): number {
  return rollWind(state.seed, 1000 + Math.floor((now - (state.dm?.startedAt ?? now)) / DM_WIND_PERIOD_MS), state.style);
}

function dmTank(state: GameState, playerId: string, now: number): Tank {
  if (state.status !== 'playing') throw new TurnError('Hra už skončila.');
  if (!state.dm) throw new TurnError('Tahle hra není deathmatch.');
  const tank = state.tanks.find((t) => t.id === playerId);
  if (!tank) throw new TurnError('V této hře nehraješ.');
  if (!tank.alive) throw new TurnError('Tvůj tank je zničený.');
  return tank;
}

/** Deathmatch: výstřel (s případnou jízdou) kdykoli, když je nabito. */
export function resolveAction(
  state: GameState,
  terrain: Terrain,
  playerId: string,
  input: TurnInput,
  now: number,
): { state: GameState; events: TurnEvents } {
  const t0 = dmTank(state, playerId, now);
  if (now < (t0.reloadAt ?? 0)) throw new TurnError('Ještě nabíjíš.');
  checkInput(t0, input, dmFuel(t0, now));
  if (input.weapon === 'guided' || input.weapon === 'kamikaze' || input.weapon === 'bomber' || input.steer) throw new TurnError('Řízené zbraně v deathmatchi nejdou.');
  const next: GameState = structuredClone(state);
  const tank = next.tanks.find((t) => t.id === playerId)!;
  tank.y = settleY(terrain, tank.x, tank.y);
  const ev = emptyEvents('shot', playerId);
  const cleanInput = sanitizeInput(input);
  ev.input = cleanInput;
  tank.fuel = dmFuel(tank, now) - Math.abs(cleanInput.move);
  tank.fuelAt = now;
  tank.reloadAt = now + DM_RELOAD_MS;
  next.wind = dmWind(next, now);
  ev.duration = playAction(next, terrain, tank, cleanInput, ev, true);
  dmAfter(next, ev, now);
  return { state: next, events: ev };
}

/** Deathmatch: jen jízda (výstřel nečeká na nabití). */
export function resolveDrive(
  state: GameState,
  terrain: Terrain,
  playerId: string,
  move: number,
  now: number,
): { state: GameState; events: TurnEvents } {
  const t0 = dmTank(state, playerId, now);
  if (!Number.isInteger(move) || move === 0) throw new TurnError('Neplatný pohyb.');
  if (Math.abs(move) > dmFuel(t0, now)) throw new TurnError('Málo paliva.');
  const next: GameState = structuredClone(state);
  const tank = next.tanks.find((t) => t.id === playerId)!;
  tank.y = settleY(terrain, tank.x, tank.y);
  const ev = emptyEvents('drive', playerId);
  tank.fuel = dmFuel(tank, now) - Math.abs(move);
  tank.fuelAt = now;
  ev.duration = playAction(next, terrain, tank, { move, angle: tank.angle, power: tank.power, weapon: 'shell' }, ev, false);
  dmAfter(next, ev, now);
  return { state: next, events: ev };
}

/** Deathmatch: vypršel čas zápasu → vyhodnotit. */
export function endDeathmatch(state: GameState, now: number): { state: GameState; events: TurnEvents } {
  const next: GameState = structuredClone(state);
  const ev = emptyEvents('end', '');
  ev.duration = 1;
  if (next.dm && next.status === 'playing') {
    next.dm.endsAt = Math.min(next.dm.endsAt, now);
    checkDmEnd(next, now);
  }
  return { state: next, events: ev };
}

/** Připíše zabití a škody, posune počítadlo akcí a zkontroluje konec. */
function dmAfter(next: GameState, ev: TurnEvents, now: number) {
  const scores = next.dm!.scores;
  const score = (id: string) => (scores[id] ??= { kills: 0, damage: 0 });
  for (const d of ev.damage) {
    const by = d.by ?? ev.playerId;
    if (by !== d.id) score(by).damage += d.amount;
  }
  for (const d of ev.deaths) {
    const by = d.by ?? ev.playerId;
    if (by !== d.id) score(by).kills++;
  }
  next.turn++;
  checkDmEnd(next, now);
}

/** Konec: zbyl nejvýš jeden tank, všem živým došla munice, nebo vypršel čas. */
function checkDmEnd(state: GameState, now: number): boolean {
  const dm = state.dm!;
  const alive = state.tanks.filter((t) => t.alive);
  const noAmmo = alive.every((t) => totalAmmo(t.ammo) === 0);
  if (alive.length > 1 && !noAmmo && now < dm.endsAt) return false;
  state.status = 'finished';
  if (alive.length === 1) {
    state.winner = alive[0].id;
  } else {
    const ranked = state.tanks
      .map((t) => ({ id: t.id, ...(dm.scores[t.id] ?? { kills: 0, damage: 0 }) }))
      .sort((a, b) => b.kills - a.kills || b.damage - a.damage);
    const [a, b] = ranked;
    state.winner = a && !(b && a.kills === b.kills && a.damage === b.damage) ? a.id : null;
  }
  return true;
}

/** Vyřadí hráče (vypršel čas / vzdal se). */
export function eliminatePlayer(
  state: GameState,
  playerId: string,
  cause: Extract<DeathCause, 'timeout' | 'surrender'>,
  /** Serverový čas (jen pro deathmatch). */
  now = Date.now(),
): { state: GameState; events: TurnEvents } {
  const next: GameState = structuredClone(state);
  const idx = next.tanks.findIndex((t) => t.id === playerId);
  const ev = emptyEvents(cause, playerId);
  const tank = next.tanks[idx];
  if (!tank || !tank.alive || next.status !== 'playing') return { state: next, events: ev };
  tank.alive = false;
  ev.deaths.push({ t: 0.2, id: playerId, cause });
  ev.duration = 1.5;
  if (next.dm) {
    next.turn++;
    checkDmEnd(next, now);
  } else if (idx === next.current) {
    finishTurn(next);
  } else {
    checkWinner(next);
  }
  return { state: next, events: ev };
}

function checkWinner(state: GameState): boolean {
  const alive = state.tanks.filter((t) => t.alive);
  if (alive.length <= 1) {
    state.status = 'finished';
    state.winner = alive[0]?.id ?? null;
    return true;
  }
  return false;
}

function finishTurn(state: GameState) {
  if (checkWinner(state)) return;
  const n = state.tanks.length;
  for (let i = 1; i <= n; i++) {
    const idx = (state.current + i) % n;
    if (state.tanks[idx].alive) {
      state.current = idx;
      break;
    }
  }
  state.turn++;
  state.wind = rollWind(state.seed, state.turn, state.style);
}
